/**
 * Boom Bap Bundle Vol. 2 - Shopify JavaScript
 * Copy this file to your theme's assets/ folder
 * Include in theme.liquid: <script src="{{ 'boom-bap-v2.js' | asset_url }}" defer></script>
 */

(function() {
  'use strict';

  // ============================================
  // AUDIO PLAYER
  // ============================================
  class AudioPlayer {
    constructor() {
      this.currentlyPlaying = null;
      this.audioElements = {};
      this.progressIntervals = {};
      this.init();
    }

    init() {
      document.addEventListener('click', (e) => {
        const playBtn = e.target.closest('[data-audio-play]');
        if (playBtn) {
          this.handlePlayClick(playBtn);
        }

        const waveform = e.target.closest('[data-audio-waveform]');
        if (waveform) {
          this.handleWaveformClick(e, waveform);
        }

        const scrubber = e.target.closest('[data-audio-scrubber]');
        if (scrubber) {
          this.handleScrubberClick(e, scrubber);
        }
      });
    }

    handlePlayClick(btn) {
      const audioId = btn.dataset.audioPlay;
      const audioUrl = btn.dataset.audioUrl;

      if (!audioUrl) return;

      if (this.currentlyPlaying === audioId) {
        this.pause(audioId);
      } else {
        if (this.currentlyPlaying) {
          this.pause(this.currentlyPlaying);
        }
        this.play(audioId, audioUrl);
      }
    }

    play(audioId, audioUrl) {
      let audio = this.audioElements[audioId];

      if (!audio) {
        audio = new Audio(audioUrl);
        this.audioElements[audioId] = audio;

        audio.addEventListener('loadedmetadata', () => {
          this.updateDuration(audioId, audio.duration);
        });

        audio.addEventListener('ended', () => {
          this.onEnded(audioId);
        });
      }

      audio.play();
      this.currentlyPlaying = audioId;
      this.setPlayingState(audioId, true);
      this.startProgressUpdate(audioId, audio);
    }

    pause(audioId) {
      const audio = this.audioElements[audioId];
      if (audio) {
        audio.pause();
      }
      this.currentlyPlaying = null;
      this.setPlayingState(audioId, false);
      this.stopProgressUpdate(audioId);
    }

    onEnded(audioId) {
      this.currentlyPlaying = null;
      this.setPlayingState(audioId, false);
      this.stopProgressUpdate(audioId);
      this.updateProgress(audioId, 0);
    }

    setPlayingState(audioId, isPlaying) {
      const playerContainer = document.querySelector(`[data-audio-player="${audioId}"]`);
      const playBtn = document.querySelector(`[data-audio-play="${audioId}"]`);
      if (playerContainer) {
        playerContainer.classList.toggle('is-playing', isPlaying);
      }
      if (playBtn) {
        playBtn.classList.toggle('is-playing', isPlaying);
        const playIcon = playBtn.querySelector('.play-icon');
        const pauseIcon = playBtn.querySelector('.pause-icon');
        if (playIcon) playIcon.style.display = isPlaying ? 'none' : 'block';
        if (pauseIcon) pauseIcon.style.display = isPlaying ? 'block' : 'none';
      }
    }

    startProgressUpdate(audioId, audio) {
      this.stopProgressUpdate(audioId);
      this.progressIntervals[audioId] = setInterval(() => {
        if (audio.duration) {
          const progress = audio.currentTime / audio.duration;
          this.updateProgress(audioId, progress);
        }
      }, 50);
    }

    stopProgressUpdate(audioId) {
      if (this.progressIntervals[audioId]) {
        clearInterval(this.progressIntervals[audioId]);
        delete this.progressIntervals[audioId];
      }
    }

    updateProgress(audioId, progress) {
      // Waveform bars (for pack preview players)
      const waveform = document.querySelector(`[data-audio-waveform="${audioId}"]`);
      if (waveform) {
        const bars = waveform.querySelectorAll('.ah-waveform-bar');
        bars.forEach((bar, i) => {
          const barProgress = i / bars.length;
          const isActive = barProgress < progress;
          bar.classList.toggle('is-active', isActive);

          // Blue palette: cool gray inactive, bright blue-white active
          bar.style.setProperty(
            'background-color',
            isActive ? 'rgba(200, 210, 230, 0.9)' : 'rgba(140, 155, 180, 0.25)',
            'important'
          );
        });
      }

      // Scrubber bar (for master preview player)
      const scrubber = document.querySelector(`[data-audio-scrubber="${audioId}"]`);
      if (scrubber) {
        const fill = scrubber.querySelector('.ah-scrubber-fill');
        if (fill) {
          fill.style.setProperty('width', `${progress * 100}%`, 'important');
        }
      }

      // Update duration display with current time for scrubber-style players
      const audio = this.audioElements[audioId];
      if (audio && audio.duration) {
        const durationEl = document.querySelector(`[data-audio-duration="${audioId}"]`);
        if (durationEl && durationEl.textContent.includes('/')) {
          durationEl.textContent = `${this.formatTime(audio.currentTime)} / ${this.formatTime(audio.duration)}`;
        }
      }
    }

    updateDuration(audioId, duration) {
      const durationEl = document.querySelector(`[data-audio-duration="${audioId}"]`);
      if (durationEl) {
        if (durationEl.textContent.includes('/')) {
          durationEl.textContent = `0:00 / ${this.formatTime(duration)}`;
        } else {
          durationEl.textContent = this.formatTime(duration);
        }
      }
    }

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    handleWaveformClick(e, waveform) {
      const audioId = waveform.dataset.audioWaveform;
      const audio = this.audioElements[audioId];
      if (!audio || !audio.duration) return;

      const rect = waveform.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const progress = Math.max(0, Math.min(1, clickX / rect.width));
      audio.currentTime = progress * audio.duration;
      this.updateProgress(audioId, progress);
    }

    handleScrubberClick(e, scrubber) {
      const audioId = scrubber.dataset.audioScrubber;
      const audio = this.audioElements[audioId];
      if (!audio || !audio.duration) return;

      const rect = scrubber.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const progress = Math.max(0, Math.min(1, clickX / rect.width));
      audio.currentTime = progress * audio.duration;
      this.updateProgress(audioId, progress);
    }
  }

  // ============================================
  // STICKY BAR
  // ============================================

  class StickyBar {
    constructor() {
      this.stickyBar = document.querySelector('.ah-sticky-bar');
      this.triggerElement = document.getElementById('audio-preview');
      this.triggerOffset = 100;
      if (this.stickyBar) {
        this.init();
      }
    }

    init() {
      window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
      this.handleScroll();
    }

    handleScroll() {
      if (!this.triggerElement) {
        const shouldShow = window.scrollY > 500;
        this.stickyBar.classList.toggle('is-visible', shouldShow);
        return;
      }

      const rect = this.triggerElement.getBoundingClientRect();
      const shouldShow = rect.top <= this.triggerOffset;
      this.stickyBar.classList.toggle('is-visible', shouldShow);
    }
  }

  // ============================================
  // FAQ ACCORDION
  // ============================================

  class FAQAccordion {
    constructor() {
      this.init();
    }

    init() {
      document.addEventListener('click', (e) => {
        const question = e.target.closest('.ah-faq-question');
        if (question) {
          this.toggle(question);
        }
      });
    }

    toggle(questionBtn) {
      const faqItem = questionBtn.closest('.ah-faq-item');
      if (!faqItem) return;

      const isOpen = faqItem.classList.contains('is-open');
      document.querySelectorAll('.ah-faq-item.is-open').forEach(item => {
        if (item !== faqItem) {
          item.classList.remove('is-open');
        }
      });

      faqItem.classList.toggle('is-open', !isOpen);
    }
  }

  // ============================================
  // LAZY LOADING WITH INTERSECTION OBSERVER
  // ============================================

  class LazyLoader {
    constructor() {
      this.init();
    }

    init() {
      const lazyElements = document.querySelectorAll('[data-lazy]');
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.load(entry.target);
              observer.unobserve(entry.target);
            }
          });
        }, {
          rootMargin: '100px'
        });

        lazyElements.forEach(el => observer.observe(el));
      } else {
        lazyElements.forEach(el => this.load(el));
      }
    }

    load(element) {
      const lazySrc = element.dataset.lazySrc;
      if (lazySrc && element.tagName === 'IMG') {
        element.src = lazySrc;
      }

      element.classList.remove('is-loading');
      element.classList.add('is-loaded');
      element.removeAttribute('data-lazy');
    }
  }

  // ============================================
  // MARQUEE ANIMATION CONTROL
  // ============================================

  class MarqueeController {
    constructor() {
      this.marquees = document.querySelectorAll('.ah-marquee');
      this.init();
    }

    init() {
      this.marquees.forEach(marquee => {
        marquee.addEventListener('mouseenter', () => {
          marquee.style.animationPlayState = 'paused';
        });
        marquee.addEventListener('mouseleave', () => {
          marquee.style.animationPlayState = 'running';
        });
      });

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.marquees.forEach(marquee => {
          marquee.style.animation = 'none';
        });
      }
    }
  }

  // ============================================
  // SCROLL ANIMATIONS
  // ============================================

  class ScrollAnimations {
    constructor() {
      this.init();
    }

    init() {
      const animatedElements = document.querySelectorAll('[data-animate-on-scroll]');
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('ah-animate-fade-in');
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.1
        });

        animatedElements.forEach(el => observer.observe(el));
      }
    }
  }

  // ============================================
  // WAVEFORM GENERATOR
  // ============================================

  function generateWaveformBars(container, data) {
    if (!container) return;

    container.style.setProperty('display', 'flex', 'important');
    container.style.setProperty('align-items', 'center', 'important');
    container.style.setProperty('justify-content', 'space-between', 'important');
    container.style.setProperty('gap', '1px', 'important');
    container.style.setProperty('height', '32px', 'important');
    container.style.setProperty('min-height', '32px', 'important');
    container.style.setProperty('box-sizing', 'border-box', 'important');
    container.style.setProperty('padding', '0 2px', 'important');
    container.style.setProperty('visibility', 'visible', 'important');
    container.style.setProperty('opacity', '1', 'important');

    // If no waveform data, make existing Liquid bars visible with blue palette
    if (!data) {
      container.querySelectorAll('.ah-waveform-bar').forEach((bar) => {
        bar.style.setProperty('opacity', '1', 'important');
        bar.style.setProperty('visibility', 'visible', 'important');
        bar.style.setProperty('display', 'block', 'important');
        bar.style.setProperty('background-color', 'rgba(140, 155, 180, 0.25)', 'important');
      });
      return;
    }

    // Interpolate data to double the bar count
    const interpolatedData = [];
    for (let i = 0; i < data.length; i++) {
      interpolatedData.push(data[i]);
      if (i < data.length - 1) {
        const avg = (data[i] + data[i + 1]) / 2;
        interpolatedData.push(avg * (0.9 + Math.random() * 0.2));
      }
    }
    interpolatedData.push(data[data.length - 1] * 0.85);

    container.innerHTML = '';
    interpolatedData.forEach((height) => {
      const bar = document.createElement('div');
      bar.className = 'ah-waveform-bar';
      bar.style.cssText = `
        height: ${Math.max(height * 100, 12)}% !important;
        min-height: 4px !important;
        flex: 1 1 0 !important;
        min-width: 1px !important;
        max-width: 3px !important;
        border-radius: 1.5px !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: block !important;
        background-color: rgba(140, 155, 180, 0.25) !important;
        transition: background-color 0.1s ease !important;
      `;

      container.appendChild(bar);
    });
  }

  // ============================================
  // SCRUBBER SAFETY STYLES
  // ============================================

  function initializeScrubbers() {
    document.querySelectorAll('.ah-player--master').forEach((player) => {
      player.style.setProperty('display', 'flex', 'important');
      player.style.setProperty('visibility', 'visible', 'important');
      player.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('[data-audio-scrubber]').forEach((scrubber) => {
      scrubber.style.setProperty('display', 'block', 'important');
      scrubber.style.setProperty('position', 'relative', 'important');
      scrubber.style.setProperty('height', '6px', 'important');
      scrubber.style.setProperty('min-height', '6px', 'important');
      scrubber.style.setProperty('width', '100%', 'important');
      scrubber.style.setProperty('border-radius', '9999px', 'important');
      scrubber.style.setProperty('overflow', 'hidden', 'important');
      scrubber.style.setProperty('background-color', 'rgba(55, 62, 69, 0.8)', 'important');
      scrubber.style.setProperty('cursor', 'pointer', 'important');
      scrubber.style.setProperty('visibility', 'visible', 'important');
      scrubber.style.setProperty('opacity', '1', 'important');

      let fill = scrubber.querySelector('.ah-scrubber-fill');
      if (!fill) {
        fill = document.createElement('div');
        fill.className = 'ah-scrubber-fill';
        scrubber.appendChild(fill);
      }

      fill.style.setProperty('display', 'block', 'important');
      fill.style.setProperty('position', 'absolute', 'important');
      fill.style.setProperty('left', '0', 'important');
      fill.style.setProperty('top', '0', 'important');
      fill.style.setProperty('bottom', '0', 'important');
      fill.style.setProperty('height', '100%', 'important');
      fill.style.setProperty('width', '0%');
      fill.style.setProperty('border-radius', '9999px', 'important');
      fill.style.setProperty('background-color', '#2850a8', 'important');
      fill.style.setProperty('visibility', 'visible', 'important');
      fill.style.setProperty('opacity', '1', 'important');
    });

    console.info('[BoomBapV2] Scrubbers initialized:', document.querySelectorAll('[data-audio-scrubber]').length);
  }

  // ============================================
  // WAVEFORM DATA
  // Replace with your actual waveform data for each preview ID
  // ============================================
  const waveformData = {
    "1-noc2-lemon": [0.8,0.47,0.32,0.61,0.77,0.56,0.36,0.47,0.63,0.43,0.55,0.5,0.47,0.5,0.5,0.53,0.38,0.78,0.61,0.38,0.49,0.78,0.66,0.44,0.34,0.65,0.51,0.47,0.94,1,0.76,0.43,0.7,0.74,0.28],
    "2-noc2-sleep-walking": [0.64,0.63,0.55,0.67,0.56,0.65,0.66,0.8,0.59,0.62,0.6,0.49,0.7,0.58,0.64,0.5,0.57,0.86,0.77,0.6,0.69,0.88,0.88,0.65,0.61,0.73,1,0.74,0.57,0.73,0.88,0.87,0.55,0.45,0.11],
    "3-noc2-the-ball": [0.86,0.44,0.63,0.48,0.68,0.33,0.56,0.59,0.66,0.82,0.46,0.62,0.47,0.64,0.3,0.62,0.55,0.75,0.71,0.74,0.93,1,0.92,0.67,0.95,0.93,0.92,0.79,0.88,0.91,0.99,0.88,0.69,0.72,0.25],
    "4-noc2-almost-home": [0.75,0.7,0.67,0.67,0.8,0.79,0.77,0.8,0.79,0.83,0.76,0.87,0.87,0.82,0.81,0.8,0.98,0.63,0.69,0.7,0.68,0.68,0.61,0.69,0.62,0.7,0.79,0.8,0.75,1,0.79,0.8,0.73,0.59,0.21],
    "1-sanc-your-grace": [0.79,0.81,0.88,0.69,0.97,0.68,0.97,0.7,0.93,0.7,0.87,0.72,0.85,0.76,0.74,0.86,0.63,1,0.69,0.95,0.72,0.99,0.86,0.78,0.95,0.76,0.84,0.61,0.77,0.68,0.51,0.44,0.38,0.21,0.08],
    "2-sanc-cant-make-it": [0.32,0.24,0.23,0.36,0.35,0.28,0.23,0.29,0.37,0.39,0.45,0.39,0.48,0.27,0.42,0.53,1,0.53,0.5,0.44,0.53,0.61,0.65,0.58,0.67,0.51,0.55,0.52,0.33,0.39,0.39,0.28,0.25,0.22,0.15],
    "3-sanc-united": [0.5,0.59,0.71,0.77,0.69,0.61,0.65,0.7,0.73,0.89,1,0.81,0.85,0.88,1,0.85,0.88,0.7,0.58,0.63,0.78,0.74,0.55,0.53,0.74,0.69,0.85,0.97,1,0.91,0.87,0.8,0.65,0.31,0.1],
    "4-sanc-candle": [0.76,0.87,0.75,0.83,0.75,0.8,0.74,0.72,0.69,0.93,0.79,0.73,0.82,0.77,0.82,0.75,0.62,0.8,0.92,0.75,0.83,0.62,1,0.97,0.8,0.89,0.89,0.85,1,0.98,0.87,0.75,0.51,0.31,0.11],
    "1-dusty-p-more-love": [0.94,0.82,0.94,0.85,0.98,0.77,0.97,0.83,1,0.84,0.79,0.71,0.47,0.86,0.69,0.81,0.56,0.79,0.79,0.75,0.7,0.66,0.76,0.45,0.95,0.56,0.87,0.72,0.77,0.62,0.73,0.74,0.6,0.34,0.1],
    "2-dusty-p-cold-winter": [1,0.51,0.64,0.58,0.88,0.66,0.57,0.67,0.73,0.88,0.53,0.64,0.61,1,0.52,0.61,0.75,0.72,0.69,0.61,0.55,0.79,0.71,0.62,0.54,0.43,0.63,0.66,0.58,0.65,0.74,0.71,0.56,0.29,0.18],
    "3-dusty-p-one-of-those-days": [0.74,0.86,0.6,0.83,0.66,0.78,0.79,0.84,0.52,0.93,0.77,0.76,0.79,0.56,1,0.81,0.87,0.66,0.56,0.37,0.61,0.51,0.7,0.22,0.48,0.57,0.55,0.47,0.41,0.59,0.55,0.51,0.24,0.28,0.45],
    "4-dusty-p-a-lie": [0.46,0.35,0.45,0.27,0.42,0.31,0.32,0.42,0.25,0.5,0.58,0.45,0.7,0.52,0.65,0.55,0.67,0.75,0.69,0.55,0.75,0.56,0.65,0.45,0.3,0.6,0.9,0.75,0.51,0.73,0.66,1,0.5,0.19,0.14],
    "1-obsid-on-the-run": [0.97,0.82,0.66,0.84,0.92,0.88,0.85,0.94,0.84,0.94,0.76,0.74,0.83,0.81,0.55,0.84,0.88,0.9,0.9,0.73,0.8,0.87,1,0.59,0.95,0.83,0.94,0.87,0.68,0.83,0.96,0.88,0.47,0.44,0.16],
    "2-obsid-bruised-flower": [0.77,0.61,0.71,0.95,0.8,0.64,0.66,0.31,0.49,0.76,0.61,0.32,1,0.84,0.49,0.23,0.25,0.44,0.66,0.61,0.81,0.87,0.66,0.65,0.47,0.23,0.79,0.58,0.45,0.83,0.89,0.7,0.27,0.11,0.05],
    "3-obsid-night-energy": [0.72,0.58,0.46,1,0.83,0.65,0.51,0.43,0.47,0.71,0.57,0.43,0.53,0.51,0.5,0.53,0.72,0.64,0.67,0.52,0.75,0.95,0.74,0.58,0.44,0.44,0.65,0.63,0.5,0.48,0.74,0.77,0.58,0.28,0.06],
    "4-obsid-angel": [0.9,0.7,0.78,0.83,1,0.74,0.75,0.95,0.74,0.88,0.73,0.82,0.9,0.91,0.7,0.77,0.77,0.82,0.8,0.75,0.81,0.89,0.92,0.79,0.83,0.89,0.93,0.72,0.76,0.81,0.89,0.95,0.65,0.38,0.12],
    "1-mus-winter": [1,0.79,0.72,0.69,0.89,0.89,0.74,0.79,0.84,1,0.79,0.72,0.64,0.89,0.88,0.73,0.81,0.84,0.98,0.78,0.7,0.61,0.91,0.88,0.73,0.8,0.77,0.96,0.78,0.69,0.6,0.81,0.58,0.32,0.11],
    "2-mus-lost-paradise": [0.63,0.73,0.91,0.77,0.65,0.67,0.85,0.78,0.92,0.53,0.73,0.98,0.96,0.7,0.71,0.88,0.77,0.92,0.65,0.83,0.83,0.61,0.54,0.48,0.96,0.7,0.85,0.7,0.78,0.93,1,0.6,0.58,0.44,0.11],
    "3-mus-exhale": [0.71,0.44,0.35,0.43,0.55,0.57,0.5,0.65,0.7,0.46,0.32,0.33,0.44,0.56,0.68,0.51,0.71,0.77,0.48,0.37,0.47,0.43,0.71,0.42,0.69,0.57,1,0.74,0.49,0.48,0.57,0.76,0.35,0.31,0.07],
    "4-mus-brief-meeting": [0.78,0.63,0.64,0.6,0.64,0.56,0.35,0.49,0.54,1,0.6,0.64,0.6,0.96,0.64,0.44,0.96,0.67,0.74,0.61,0.63,0.6,0.65,0.45,0.39,0.43,0.58,0.77,0.64,0.6,0.81,0.85,0.68,0.42,0.16],
    "1-20-breaks-room-to-grow": [0.79,0.28,0.78,0.81,0.8,0.3,0.26,1,0.53,0.78,0.29,0.78,0.82,0.79,0.25,0.28,0.79,0.79,0.79,0.28,0.26,0.98,0.92,0.32,0.28,0.79,0.82,0.25,0.78,0.3,0.78,0.81,0.8,0.2,0.07],
    "2-20-breaks-last-we-spoke": [0.73,0.19,0.97,0.28,0.14,0.69,0.23,0.72,0.74,0.45,0.73,0.46,0.27,1,0.48,0.57,0.72,0.26,0.77,0.7,0.33,0.95,0.31,0.2,0.99,0.21,0.09,0.71,0.2,0.91,0.49,0.5,0.78,0.27,0.06],
    "3-20-breaks-walls-come-down": [0.66,0.32,0.99,0.3,0.69,0.39,0.68,0.72,0.3,0.7,0.35,0.95,0.39,0.14,0.76,0.32,0.93,0.78,1,0.24,0.68,0.78,0.23,0.7,0.38,0.94,0.39,0.15,0.71,0.33,0.96,0.35,0.68,0.38,0.09],
    "4-20-breaks-dance-shoes": [0.44,0.52,0.72,0.57,0.73,0.59,0.72,0.45,0.74,0.54,0.74,0.51,0.7,0.52,0.69,0.26,0.45,0.64,0.84,0.54,0.87,0.65,0.83,0.63,0.94,0.58,0.86,0.67,0.94,0.61,1,0.96,0.85,0.34,0.36],
    "1-actually-use-black-magic": [0.52,1,0.95,0.8,0.91,0.61,0.66,0.9,0.75,0.74,0.93,0.82,0.91,1,0.82,0.9,0.64,0.67,0.84,0.73,0.74,0.81,0.82,0.9,0.99,0.84,0.9,0.65,0.67,0.84,0.69,0.78,0.76,0.65,0.26],
    "2-actually-use-everything-i-need": [0.18,0.32,0.79,0.63,0.9,0.85,0.68,0.55,1,0.79,0.84,0.45,0.45,0.84,0.63,0.93,0.93,0.47,0.92,0.85,0.62,0.84,0.37,0.79,0.62,0.99,0.84,0.68,0.55,1,0.78,0.84,0.45,0.34,0.23],
    "3-actually-use-i-go-crazy": [0.58,0.4,0.66,0.35,0.43,0.52,0.47,0.42,0.53,0.45,0.53,1,0.74,0.97,0.6,0.44,0.47,0.39,0.37,0.55,0.4,0.1,0.58,0.39,0.65,0.38,0.43,0.51,0.45,0.45,0.53,0.44,0.54,0.79,0.26],
    "4-actually-use-woman": [0.29,0.77,0.83,0.48,0.84,0.81,0.52,0.76,0.8,0.48,1,0.59,0.79,0.75,0.43,0.77,0.8,0.73,0.77,0.63,0.64,0.91,0.57,0.77,0.78,0.4,0.84,0.74,0.79,0.77,0.55,0.75,0.83,0.48,0.32],
    "1-drum-kit-hop-out": [0.87,0.73,0.87,0.86,0.67,0.66,0.64,0.89,1,0.97,0.72,0.68,1,0.67,0.78,0.61,0.54,0.97,0.94,0.95,0.81,0.66,0.97,0.65,0.78,0.65,0.76,0.95,0.93,0.85,0.78,0.88,0.81,0.58,0.76],
    "2-drum-kit-rainbows": [0.9,0.89,0.83,0.36,0.25,0.9,0.41,0.9,0.24,0.3,0.97,0.36,0.87,0.28,0.33,0.92,0.5,0.91,0.44,0.78,0.9,0.95,0.33,0.87,0.23,1,0.97,0.42,0.91,0.25,0.3,0.99,0.36,0.88,0.28],
    "3-drum-kit-same-thing": [0.74,0.75,0.71,0.36,0.6,0.54,0.3,0.42,0.41,0.72,0.28,0.66,0.5,0.93,0.61,0.33,0.74,0.65,0.87,0.26,0.71,0.36,0.81,0.53,0.26,0.38,0.82,0.8,0.4,0.45,0.32,1,0.54,0.27,0.41],
    "4-drum-kit-switzerland": [0.29,0.43,0.42,0.7,0.46,1,0.6,0.23,0.77,0.53,0.7,0.91,0.57,0.79,0.49,0.98,0.62,0.66,0.65,0.53,0.71,0.92,0.77,0.58,0.63,0.85,0.71,0.65,0.56,0.66,0.7,0.88,0.87,0.67,0.63],
    "1-stoic-breaks-encore": [0.8,0.74,0.61,0.39,0.63,0.69,0.65,0.78,0.79,0.99,0.61,0.79,0.43,0.8,0.89,0.51,0.79,0.8,0.68,0.64,0.67,0.2,0.77,0.75,0.68,0.63,0.83,1,0.54,0.73,0.61,0.95,0.67,0.54,0.25],
    "2-stoic-breaks-smoke-break-dance": [1,0.56,0.68,0.61,0.83,0.37,0.09,0.67,0.09,0.91,0.65,0.42,0.68,0.59,0.86,0.16,0.59,0.34,0.37,0.93,0.56,0.69,0.61,0.83,0.36,0.06,0.68,0.08,0.91,0.64,0.55,0.57,0.61,0.61,0.04],
    "3-stoic-breaks-incense": [0.47,0.46,0.14,0.47,0.16,0.15,0.46,0.16,0.55,0.14,0.14,0.07,0.49,0.23,0.58,0.16,0.09,0.13,0.46,0.46,0.24,0.47,0.2,0.18,0.6,0.59,1,0.74,0.21],
    "4-stoic-breaks-bounce": [0.9,0.85,0.87,0.65,0.55,0.8,0.9,0.84,1,0.62,0.72,0.84,0.42,0.77,0.84,0.47,0.52,0.73,0.86,0.75,0.87,0.67,0.88,0.51,0.66,0.56,0.86,0.44,0.55,0.86,0.84,0.71,0.84,0.49,0.3],
    "1-galaxymost-definitely": [0.39,0.4,0.43,0.34,0.47,0.31,0.64,0.66,0.45,0.36,0.43,0.39,0.39,0.42,0.38,0.81,1,0.74,0.63,0.54,0.26,0.33,0.61,0.58,0.68,0.6,0.5,0.62,0.5,0.23,0.4,0.62,0.5,0.41,0.21],
    "2-galaxyfools-gold": [0.69,0.39,0.65,0.56,0.66,0.98,0.89,0.91,0.81,0.57,0.43,0.61,0.59,0.86,1,0.71,0.96,0.64,0.49,0.86,0.49,0.23,0.54,0.73,0.67,0.25,0.48,0.43,0.74,0.47,0.58,0.51,0.36,0.27,0.02],
    "3-galaxyparadise": [0.52,0.61,0.81,0.58,0.57,0.49,0.77,0.81,0.77,0.46,0.67,0.86,0.45,0.58,0.56,0.87,0.55,0.77,0.96,0.45,0.76,0.48,1,0.56,0.79,0.49,0.58,0.43,0.5,0.58,0.68,0.66,0.42,0.23,0.07],
    "4-galaxythursday": [0.62,0.8,0.95,0.73,0.57,0.77,0.92,0.84,0.85,0.72,0.79,0.94,0.71,0.59,0.81,0.95,0.84,0.89,1,0.9,0.89,0.89,0.67,0.78,0.95,0.92,0.61,0.79,0.94,0.76,0.63,0.73,0.61,0.44,0.15],
    "1-noc-cafe": [0.63,0.44,1,0.78,0.68,0.45,0.9,0.88,0.6,0.53,0.54,0.97,0.8,0.64,0.39,1,0.78,0.65,0.46,0.85,0.82,0.61,0.56,0.41,0.8,0.54,0.64,0.42,0.94,0.73,0.66,0.48,0.41,0.33,0.11],
    "2-noc-impartial": [0.71,0.83,0.75,0.8,0.79,0.79,0.83,0.74,0.76,0.74,0.87,0.71,0.81,0.79,0.84,0.79,0.81,1,0.84,0.73,0.76,0.74,0.85,0.77,0.7,0.82,0.85,0.87,0.7,0.75,0.86,0.79,0.49,0.31,0.14],
    "3-noc-first-encounter": [0.74,0.34,0.25,0.36,0.7,0.46,0.2,0.25,0.58,0.68,0.29,0.32,0.39,0.73,0.35,0.55,1,0.48,0.55,0.69,0.47,0.73,0.46,0.72,0.89,0.8,0.5,0.64,0.6,0.59,0.65,0.45,0.63,0.75,0.17],
    "4-noc-cruise": [0.55,0.43,0.36,0.54,0.67,0.41,0.49,0.4,0.68,0.73,0.48,0.38,0.55,1,0.63,0.72,0.64,0.92,0.61,0.52,0.5,0.69,0.58,0.5,0.48,0.63,0.94,0.66,0.53,0.56,0.83,0.84,0.53,0.35,0.13],
    "1-nef-lab-2-1": [0.88,0.45,0.58,0.83,0.81,0.98,0.71,0.45,0.88,0.86,0.8,0.98,0.73,0.32,0.81,0.82,0.82,0.92,0.7,0.31,1,0.82,0.93,0.79,0.73,0.96,0.77,0.75,0.98,0.72,0.48,0.9,0.88,0.68,0.32],
    "2-nef-lab-2-2": [0.69,0.54,0.76,0.72,0.83,0.93,0.64,0.9,0.84,0.79,0.71,0.54,0.74,0.73,0.76,0.63,0.52,0.76,0.74,0.77,0.8,0.56,0.72,0.64,0.8,1,0.68,0.87,0.75,0.76,0.86,0.57,0.69,0.49,0.27],
    "3-nef-lab-2-3": [0.6,0.64,0.83,0.83,0.66,0.94,0.72,0.79,0.76,1,0.97,0.63,0.85,0.82,0.76,0.9,0.65,0.77,0.8,0.58,0.92,0.6,0.81,0.92,0.53,0.96,0.61,0.77,0.96,0.98,0.65,0.16,0.56,0.41,0.22],
    "4-nef-lab-2-4": [0.72,0.61,0.79,0.97,0.42,0.85,0.58,0.79,1,0.22,0.97,0.58,0.79,0.97,0.42,0.85,0.58,0.79,1,0.22,0.97,0.58,0.79,0.97,0.43,0.85,0.58,0.79,1,0.22,0.97,0.58,0.79,0.78,0.1],
    "1-loosies-1": [0.06,0.11,0.16,0.19,0.13,0.17,0.21,0.24,0.4,0.48,0.42,0.13,0.78,0.69,0.74,0.68,0.37,0.25,0.36,0.44,0.51,0.53,0.54,0.52,0.44,0.43,0.51,1,0.98,0.95,0.95,0.85,0.98,0.89,0.3],
    "2-loosies-2": [0.63,0.56,0.55,0.54,0.55,0.53,0.46,0.69,0.49,0.15,0.57,0.64,0.58,0.46,0.55,0.52,0.4,0.35,0.61,0.83,1,0.9,0.65,0.52,0.58,0.43,0.43,0.42,0.51,0.57,0.61,0.62,0.6,0.51,0.19],
    "3-loosies-3": [0.6,0.14,0.06,0.05,0.03,0.05,0.06,0.12,0.04,0.01,0.03,0.05,0.1,0.18,0.26,0.47,0.49,0.46,0.29,0.63,0.9,0.26,0.1,0.15,0.18,0.34,0.82,0.44,0.63,1,0.88,1,0.83,0.86,0.3],
    "4-loosies-4": [0.12,0.41,0.46,0.28,0.18,0.11,0.14,0.25,0.52,0.52,0.74,1,0.58,0.45,0.28,0.64,1,0.78,0.23,0.52,0.38,0.3,0.07,0.41,0.09,0.4,0.44,0.07,0.08,0.22,0.24,0.23,0.31,0.33,0.12],
    "1-sic-beat-construction": [0.8,0.25,0.71,0.23,0.71,0.24,0.71,0.74,0.74,0.31,0.5,0.49,0.23,0.77,0.24,0.67,0.33,0.77,0.33,0.71,0.24,0.65,0.3,0.22,1,0.23,0.77,0.24,0.7,0.23,0.75,0.28,0.03,0.46,0.02],
    "2-sic-detroit-soul": [0.98,0.82,0.9,0.56,0.8,0.69,0.13,0.79,0.77,0.39,0.62,0.68,0.47,0.74,0.41,0.72,0.5,0.8,0.94,1,0.55,0.79,0.44,0.67,0.7,0.42,0.79,0.73,0.12,0.8,0.79,0.41,0.39,0.48,0.09],
    "3-sic-sore-eyes": [1,0.39,1,0.4,0.97,0.45,0.16,0.8,0.79,0.38,0.19,0.72,0.39,1,0.39,0.71,0.42,0.77,0.8,0.72,0.79,0.71,0.76,0.32,0.72,0.5,0.72,0.39,0.16,0.8,0.72,0.79,0.17,0.5,0.08],
    "4-sic-soul-makossa": [0.97,0.72,0.91,0.72,0.83,0.88,0.85,0.77,0.69,1,0.41,0.83,0.72,0.98,0.72,0.85,0.73,0.8,0.98,0.61,0.99,0.62,0.96,0.45,0.85,0.77,0.96,0.73,0.63,0.96,0.71,0.98,0.59,0.56,0.11]
  };

  // Initialize all waveforms with pre-defined data
  function initializeWaveforms() {
    const containers = Array.from(document.querySelectorAll('[data-audio-waveform]'));
    const missing = [];

    containers.forEach((container) => {
      const audioId = container.dataset.audioWaveform;
      const data = waveformData[audioId];
      generateWaveformBars(container, data);

      if (!data) {
        missing.push(audioId);
      }
    });

    if (missing.length) {
      console.warn('[BoomBapV2] Missing waveformData keys for:', missing);
    }
    console.info('[BoomBapV2] Waveform containers found:', containers.length);
  }

  // ============================================
  // INITIALIZE ON DOM READY
  // ============================================

  function init() {
    new AudioPlayer();
    new StickyBar();
    new FAQAccordion();
    new LazyLoader();
    new MarqueeController();
    new ScrollAnimations();
    initializeScrubbers();
    initializeWaveforms();
  }

  // Guard against multiple initializations (unique to v2)
  if (window.BoomBapV2Initialized) return;
  window.BoomBapV2Initialized = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export for external use
  window.BoomBapV2 = {
    AudioPlayer,
    StickyBar,
    FAQAccordion,
    waveformData,
    generateWaveformBars
  };

})();
